/* ==========================================================================
   Products 2 Perform — motion layer
   GSAP + ScrollTrigger for scroll-driven reveals.
   Loaded once from layout/theme.liquid (deferred, so libs are ready).

   Fails safe: if GSAP never loads, CSS keeps every .p2p-reveal visible.
   ========================================================================== */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Keep --p2p-vw equal to the real viewport width (no scrollbar), so
  // full-bleed sections line up exactly instead of overflowing a few px.
  function syncViewportWidth() {
    var w = document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--p2p-vw', w + 'px');
  }

  syncViewportWidth();
  window.addEventListener('resize', syncViewportWidth, { passive: true });
  window.addEventListener('orientationchange', syncViewportWidth);

  function showEverything() {
    document.querySelectorAll('.p2p-reveal').forEach(function (el) {
      el.classList.add('is-in');
    });
  }

  // ------------------------------------------------------- Scroll container
  // Horizon does not scroll the window. It scrolls a .page-wrapper div, which
  // is why html/body measure exactly one viewport tall on every page.
  //
  // Lenis used to be initialised here against its default wrapper (window).
  // It called preventDefault() on every wheel event and then scrolled a window
  // that has nothing to scroll, so the mouse wheel did nothing at all on the
  // live site. Momentum scrolling is not worth a page you cannot scroll, so
  // it is gone; the theme's own native scrolling is what runs now.
  function getScroller() {
    return document.querySelector('.page-wrapper') || window;
  }

  // ------------------------------------------------------------ Reveals
  function initReveals(gsap, ScrollTrigger, scope) {
    var root = scope || document;
    var items = root.querySelectorAll('.p2p-reveal:not(.is-in)');
    if (!items.length) return;

    // Batch so everything entering together animates as one staggered group
    // rather than N independent tweens.
    ScrollTrigger.batch(items, {
      start: 'top 88%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          x: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.09,
          overwrite: true,
          onComplete: function () {
            this.targets().forEach(function (el) {
              el.classList.add('is-in');
              // Hand control back to CSS so hover transitions aren't fighting
              // inline transforms left behind by GSAP.
              el.style.removeProperty('transform');
              el.style.removeProperty('filter');
              el.style.removeProperty('opacity');
            });
          }
        });
      }
    });
  }

  // Safety net.
  // ScrollTrigger's cached scroll position can still go stale, and
  // late-decoding product images shift layout underneath it. When that
  // happens a batch never fires and the section is left at opacity 0 —
  // content simply missing. This watches every reveal target and, if one is
  // still invisible a beat after it has entered the viewport, hands it back
  // to CSS. GSAP keeps the animation in the normal case; this only catches
  // the ones it dropped.
  function initRevealSafetyNet(scope) {
    var root = scope || document;
    var items = root.querySelectorAll('.p2p-reveal');
    if (!items.length || typeof IntersectionObserver !== 'function') return;

    function show(el) {
      if (el.classList.contains('is-in')) return;
      el.classList.add('is-in');
      el.style.removeProperty('transform');
      el.style.removeProperty('filter');
      el.style.removeProperty('opacity');
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          show(entry.target);
        });
      },
      { rootMargin: '0px 0px -5% 0px' }
    );

    items.forEach(function (el) {
      io.observe(el);
    });
  }



  // ------------------------------------------------- Nav over a video hero
  // The bar is fixed and transparent over the footage, and turns solid white
  // once the page moves. The listener goes on .page-wrapper because that, not
  // the window, is what scrolls in this theme.
  function initNavOverHero() {
    if (!document.querySelector('.p2p-hero--video')) return;

    var header = document.querySelector('header.shopify-section-group-header-group');
    if (header) {
      var setH = function () {
        document.documentElement.style.setProperty(
          '--p2p-header-h',
          Math.round(header.getBoundingClientRect().height) + 'px'
        );
      };
      setH();
      window.addEventListener('resize', setH, { passive: true });
    }

    // Which element actually scrolls depends on the viewport: the theme's
    // .page-wrapper takes it on desktop, the document takes it on a phone.
    // Watching only one meant the bar never went solid on the other, so read
    // whichever has moved and listen to both.
    var pageWrapper = document.querySelector('.page-wrapper');
    var root = document.documentElement;
    var ticking = false;

    function currentY() {
      var win = window.scrollY || document.documentElement.scrollTop || 0;
      var wrap = pageWrapper ? pageWrapper.scrollTop : 0;
      return Math.max(win, wrap);
    }

    function apply() {
      root.classList.toggle('p2p-nav-solid', currentY() > 40);
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (pageWrapper) pageWrapper.addEventListener('scroll', onScroll, { passive: true });
  }

  // --------------------------------------------------------- Hero intro
  function initHero(gsap) {
    var hero = document.querySelector('[data-p2p-hero]');
    if (!hero) return;

    var lines = hero.querySelectorAll('[data-hero-line]');
    var fades = hero.querySelectorAll('[data-hero-fade]');
    var media = hero.querySelector('[data-hero-media]');
    var badge = hero.querySelector('[data-hero-badge]');

    // In the theme editor, sections re-render constantly. An intro tween that
    // gets interrupted mid-flight strands elements at their start values (the
    // hero image disappears). Merchants want to see the finished layout anyway.
    if (window.Shopify && window.Shopify.designMode) {
      gsap.set([lines, fades, media, badge], { clearProps: 'all' });
      return;
    }

    if (hero.dataset.p2pHeroPlayed === '1') return;
    hero.dataset.p2pHeroPlayed = '1';

    // fromTo (not from) so both ends are explicit — nothing can be left stuck.
    var tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: function () {
        gsap.set([lines, fades, media, badge], { clearProps: 'all' });
      }
    });

    tl.fromTo(lines, { yPercent: 115 }, { yPercent: 0, duration: 1.05, stagger: 0.1 })
      .fromTo(
        fades,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.09 },
        '-=0.6'
      )
      .fromTo(
        media,
        { opacity: 0, scale: 0.92 },
        { opacity: 1, scale: 1, duration: 1.2, ease: 'power2.out' },
        '-=1.0'
      )
      .fromTo(
        badge,
        { opacity: 0, y: 16, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6 },
        '-=0.5'
      );

    return tl;
  }

  // ------------------------------------------------- Scroll-linked depth
  function initScrub(gsap, ScrollTrigger) {
    // Hero product drifts up and the glow scales as you scroll past — cheap
    // depth cue, all transform/opacity so it stays on the compositor.
    document.querySelectorAll('[data-p2p-scrub]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-p2p-scrub')) || 60;
      gsap.to(el, {
        y: -amount,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('[data-p2p-hero]') || el,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6
        }
      });
    });

    // Section headings get a subtle scrub as they pass through.
    document.querySelectorAll('[data-p2p-drift]').forEach(function (el) {
      gsap.fromTo(
        el,
        { y: 28 },
        {
          y: -28,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        }
      );
    });
  }

  // ------------------------------------------------------------- Counters
  function initCounters(gsap, ScrollTrigger) {
    document.querySelectorAll('[data-p2p-count]').forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-p2p-count'));
      if (isNaN(target)) return;
      var suffix = el.getAttribute('data-p2p-count-suffix') || '';
      var obj = { v: 0 };

      ScrollTrigger.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.to(obj, {
            v: target,
            duration: 1.4,
            ease: 'power2.out',
            onUpdate: function () {
              el.textContent = Math.round(obj.v) + suffix;
            }
          });
        }
      });
    });
  }

  // ------------------------------------------------------------------ go
  function boot() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    if (!gsap || !ScrollTrigger) {
      showEverything();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    var designMode = !!(window.Shopify && window.Shopify.designMode);

    // Reduced motion, or the theme editor (where half-played tweens on
    // re-rendered sections read as broken layout): show the finished page.
    if (reduced || designMode) {
      showEverything();
      initHero(gsap);
      initCounters(gsap, ScrollTrigger);
      return;
    }

    var scroller = getScroller();
    if (scroller !== window && ScrollTrigger.defaults) {
      ScrollTrigger.defaults({ scroller: scroller });
    }

    initNavOverHero();
    initHero(gsap);
    initRevealSafetyNet();
    initScrub(gsap, ScrollTrigger);
    initCounters(gsap, ScrollTrigger);

    // Product/hero images change layout height once decoded.
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });

    // Theme editor swaps sections in and out.
    document.addEventListener('shopify:section:load', function (e) {
      initHero(gsap);
      initRevealSafetyNet(e.target);
      initScrub(gsap, ScrollTrigger);
      ScrollTrigger.refresh();
    });
  }

  // ------------------------------------------------- Autoplay on mobile
  // The videos all carry autoplay/muted/playsinline, but a phone still
  // refuses to start one that was off-screen at load, and Low Power Mode
  // refuses outright - you get the poster frame and assume the video is
  // missing. So: ask again when the video scrolls into view, and once more
  // on the first touch anywhere, which counts as the user gesture iOS wants.
  function initVideoAutoplay() {
    var vids = [].slice.call(document.querySelectorAll('video[autoplay]'));
    if (!vids.length) return;

    function tryPlay(v) {
      if (!v.paused) return;
      v.muted = true;           // some browsers drop this on re-attach
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) tryPlay(e.target);
        });
      }, { rootMargin: '120px' });
      vids.forEach(function (v) { io.observe(v); });
    } else {
      vids.forEach(tryPlay);
    }

    function onFirstTouch() {
      vids.forEach(tryPlay);
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
    }
    window.addEventListener('touchstart', onFirstTouch, { passive: true, once: true });
    window.addEventListener('click', onFirstTouch, { once: true });

    // Coming back from a background tab pauses them on some phones.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) vids.forEach(tryPlay);
    });
  }

  initVideoAutoplay();
  document.addEventListener('shopify:section:load', initVideoAutoplay);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Safety net: if the CDN is blocked or slow, never leave content hidden.
  setTimeout(function () {
    if (!window.gsap) showEverything();
  }, 2500);
})();
